<template>
  <div class="mb-7">
    <div class="flex align-items-center justify-content-between container-items">
      <div>
        <TTableFilters
          class="ml-3 mt-3"
          apiversion="v2"
          :filtersFields="filtersFields"
          :dropdown="dropdown"
          :alternativeStylesCleanButton="true"
          @search="() => getData()"
          :enableFilterMultiple="true"
          ref="Filters"
        />
      </div>
      <Dropdown
        v-model="captureFormat"
        optionLabel="name"
        :options="captureFormatList"
        optionValue="action"
        placeholder="Exportar datos"
        class="mt-6 capture-format-dropdown"
        @change="handleSelection"
      />
    </div>
    <div class="flex align-items-center container-items">
      <BlockUI :blocked="loadingQuery" style="position: relative">
        <div class="block-container">
          <Card class="w-20rem">
            <template #content>
              <div class="header-actuaciones-list">
                <b><div class="text-xs mt-1">Actuaciones</div></b>
              </div>
              <div class="content-actuaciones-list overflow-auto">
                <div @click="highlightActuationOnMap">
                  <Listbox
                    v-model="actuationSelected"
                    :options="actuationsList"
                    dataKey="Id"
                    optionValue="Id"
                    optionLabel="name"
                  >
                    <template #option="slotProps">
                      <div class="flex align-items-center justify-content-between text-xs">
                        <p
                          :style="{ color: slotProps.option.name === 'Centro Civico' ? 'red' : '' }"
                        >
                          {{
                            slotProps.option.name === "Centro Civico"
                              ? "No existen actuaciones disponibles"
                              : slotProps.option.name
                          }}
                        </p>
                        <p>
                          {{ slotProps.option.number }}
                        </p>
                      </div>
                      <div
                        class="flex justify-content-between"
                        v-if="slotProps.option.name !== 'Centro Civico'"
                      >
                        <div class="flex text-xs -mt-2">
                          <i class="pi pi-tag mr-2" style="margin-top: 11px"></i>
                          <p>{{ slotProps.option.date }}</p>
                        </div>
                        <div
                          :class="getClass(slotProps.option.state)"
                          class="text-xs"
                          style="height: 25px"
                        >
                          {{ slotProps.option.state }}
                        </div>
                      </div>
                    </template>
                  </Listbox>
                </div>
              </div>
            </template>
          </Card>
          <i v-if="loadingQuery" class="pi pi-spinner pi-spin custom-spinner"></i>
        </div>
      </BlockUI>

      <BlockUI :blocked="loadingQuery" style="position: relative; width: 100%; height: 100%">
        <div class="map" style="position: relative">
          <transition name="fade">
            <div v-if="emptyActuations" class="mb-1 font-semibold dialog dialog-empty-actuations">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                style="width: 16px; height: 16px; margin-right: 6px"
                viewBox="0 0 24 24"
                fill="#f8e0e0"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="transparent"
                  stroke="#f8e0e0"
                  stroke-width="2"
                ></circle>
                <line
                  x1="8"
                  y1="8"
                  x2="16"
                  y2="16"
                  stroke="#f8e0e0"
                  stroke-width="2"
                  stroke-linecap="round"
                ></line>
                <line
                  x1="16"
                  y1="8"
                  x2="8"
                  y2="16"
                  stroke="#f8e0e0"
                  stroke-width="2"
                  stroke-linecap="round"
                ></line>
              </svg>
              <span class="text-xs"
                >No existen actuaciones disponibles para el rango de fechas seleccionado.</span
              >
            </div>
          </transition>
          <MyMap id="map" ref="map" />
        </div>
      </BlockUI>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { onBeforeRouteLeave } from "vue-router/composables";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import MyMap from "@/components/MyMap.vue";
import TTableFilters from "@/components/TTable/TTableFilters.vue";
import { useStoreCompat } from "@/composables/useStoreCompat";
import { useRest } from "@/composables/useRest";
import { useToast } from "@/composables/useToast";
import generalFunctions from "../store/modules/generalFunctions.js";

const store = useStoreCompat();
const rest = useRest();
const toast = useToast();

const Filters = ref(null);
const map = ref(null);

const emptyActuations = ref(false);
const response = ref([]);
const actuationsList = ref([]);
const actuationSelected = ref({});
const loadingQuery = ref(false);
const captureFormat = ref({});

const filtersFields = [
  {
    name: "Actuaciones",
    field: "createdAt",
    type: "multipleDays",
  },
  {
    name: "Fechas",
    field: " createdAt ",
    type: "dateOnlyRange",
  },
  {
    name: "Tipo de Actuación",
    field: "exp_pre_TipoActuacion.TipoActuacionID",
    hideOperators: true,
  },
];

const dropdown = ref({
  createdAt: {
    optionValue: "value",
    optionLabel: "name",
    options: [
      { name: "7 días", value: 7 },
      { name: "15 días", value: 15 },
      { name: "30 días", value: 30 },
    ],
  },
  "exp_pre_TipoActuacion.TipoActuacionID": {
    optionValue: "value",
    optionLabel: "name",
    options: [],
  },
});

const views = computed(() => store.state.sumarioViews);
const user = computed(() => store.state.user);
const denunciations = computed(() => store.getters["sumarioList/getList"]);

const generateFilterOptionsForFiscalUser = (options) => {
  const proxy = Filters.value;
  if (proxy && typeof proxy.generateFilterOptionsForFiscalUser === "function") {
    return proxy.generateFilterOptionsForFiscalUser(options);
  }
  return options;
};

const filterDropdownOptions = computed(() => {
  let options = [
    { name: "Expedientes", value: "Expediente" },
    { name: "Sumarios", value: "Sumario" },
    { name: "UFI", value: "UNIDAD" },
    { name: "Actuaciones Preliminares - TODAS", value: "Actuacion Preliminar" },
    {
      name: "UFI Delitos Especiales - TODOS",
      value: "Unidades Fiscales de Investigacion D.E",
    },
  ];
  let filterName = "";
  for (const actuationName in views.value) {
    filterName = Object.keys(views.value[actuationName]?.filters || {})[0];
    options.push({
      name: filterName,
      value: views.value[actuationName]?.filters?.[filterName]?.actuacionesFilters?.value,
    });
  }
  options = options.filter((obj) => !Object.values(obj).some((value) => value === undefined));
  if (user.value.userdata.claseDeUsuario === "UsuarioFiscal")
    return generateFilterOptionsForFiscalUser(options);
  return options;
});

watch(response, (newValue) => {
  const actuationsAmount = Array.isArray(newValue?.data?.data) ? newValue.data.data.length : 0;
  emptyActuations.value = actuationsAmount === 0;
});

const getTooltipMapActuationName = (nameToChange) => {
  const actuationName = generalFunctions.translateNameFromFormType(nameToChange);
  return views.value[actuationName]?.actuationName[nameToChange];
};

const choosePinIcon = (actuationType) => {
  if (actuationType?.includes("Unidades")) return require("../assets/map-pins/yellow-pin.png");
  if (actuationType?.includes("Sumario")) return require("../assets/map-pins/blue-pin.png");
  if (actuationType?.includes("Preliminar")) return require("../assets/map-pins/green-pin.png");
  if (actuationType?.includes("Expediente")) return require("../assets/map-pins/red-pin.png");
  return "";
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const buildALocationTooltip = (currentLocation) => ({
  Id: currentLocation.index,
  position: {
    lat: currentLocation.HechoubicacionLatitud,
    lng: currentLocation.HechoubicacionLongitud,
  },
  name: `${getTooltipMapActuationName(currentLocation.TiposumarioID)} <br> ${currentLocation.Numero}`,
  icon: choosePinIcon(currentLocation.TiposumarioID),
});

const buildALocationList = (currentLocation) => ({
  Id: currentLocation.index,
  name: `${getTooltipMapActuationName(currentLocation.TiposumarioID)}`,
  number: `${currentLocation.Numero}`,
  state: currentLocation.Estado,
  date: formatDate(currentLocation.FechaCreacion),
  position: {
    lat: currentLocation.HechoubicacionLatitud,
    lng: currentLocation.HechoubicacionLongitud,
  },
  caseTitle: currentLocation.CausaCaratula,
});

const locationBuilders = {
  Tooltip: buildALocationTooltip,
  List: buildALocationList,
};

const verifyEmptyActuations = (newLocationsFormated) => {
  if (newLocationsFormated.length === 0) {
    emptyActuations.value = true;
    return [
      {
        Id: 0,
        name: "Centro Civico",
        position: { lat: -31.5417287, lng: -68.53757 },
      },
    ];
  }
  emptyActuations.value = false;
  return newLocationsFormated;
};

const hasValidCoordinates = (location) => {
  const lat = Number(location?.HechoubicacionLatitud);
  const lng = Number(location?.HechoubicacionLongitud);
  return Number.isFinite(lat) && Number.isFinite(lng);
};

const formatLocations = (allLocations, formatType = "Tooltip") => {
  const builder = locationBuilders[formatType];
  const validLocations = (allLocations || []).filter(hasValidCoordinates);
  const newLocationsFormated = validLocations.map((location, index) => {
    location.index = index;
    return builder(location);
  });
  return verifyEmptyActuations(newLocationsFormated);
};

const centerTheMapToDefaultPosition = () => {
  map.value.zoom = 11;
  map.value.center = { lat: -31.518233645038876, lng: -68.48564548984253 };
};

const normalizeSearch = async (search) =>
  search?.search?.replace(" createdAt ", "createdAt").replace('"undefined":[{"value":""}],', "");

const setDataInSumarioList = async (search) => {
  await store.dispatch("sumarioList/setSearch", search);
  await store.dispatch("sumarioList/get", { accion: "inicio", addLocation: true });
  await store.dispatch("sumarioList/setLimit", null);
};

const processLocations = () => {
  map.value.markers = formatLocations(denunciations.value);
  actuationsList.value = formatLocations(denunciations.value, "List");
  loadingQuery.value = false;
};

const searchForbiddenFilterAndDeleteIt = () => {
  const allFilters = Filters.value?.filters;
  const indexToDelete = allFilters?.findIndex((filter) => filter?.operator?.value === "LAST_DAYS");
  const filterRecentlyAdded = Filters.value?.filters[Filters.value?.filters.length - 1];
  const datesAmount = allFilters?.filter((filter) => filter?.field?.name === "Fechas").length;
  const actuationsTypesAmount = allFilters?.filter(
    (filter) => filter?.field?.name === "Tipo de Actuación"
  ).length;
  if (indexToDelete === -1) return false;
  if (
    actuationsTypesAmount >= 1 &&
    datesAmount === 0 &&
    filterRecentlyAdded.operator?.value === "LAST_DAYS"
  )
    return false;
  if (
    filterRecentlyAdded.operator?.value === "LAST_DAYS" ||
    filterRecentlyAdded.field.name === "Fechas"
  ) {
    Filters.value.filters.splice(indexToDelete, 1);
    return true;
  }
};

const determineUseFromCloseFilterButton = async () => {
  const filterContentText = Filters.value.$refs.filterChip?.[0]?.children?.[0]?.innerHTML;
  const filtersAmount = Filters.value.filterChipCount;
  if (filterContentText === undefined) return;
  if (filtersAmount === 1 && filterContentText === "Actuaciones incluir 7 días") {
    Filters.value.$refs.filterChip[0].children[1].disabled = true;
  } else {
    Filters.value.$refs.filterChip[0].children[1].disabled = false;
  }
};

// eslint-disable-next-line no-use-before-define
async function getByPredeterminedDays(search) {
  if (Filters.value.filterChipCount > 1 && searchForbiddenFilterAndDeleteIt()) {
    // eslint-disable-next-line no-use-before-define
    getData();
    return;
  }
  let effectiveSearch = search;
  if (effectiveSearch === undefined) {
    effectiveSearch = { createdAt: [{ operator: "LAST_DAYS", value: 7 }] };
    Filters.value.field = {
      name: "Actuaciones",
      field: "createdAt",
      type: "multipleDays",
    };
    Filters.value.operator = { value: "LAST_DAYS", type: "INCLUIR" };
    Filters.value.value = { name: "7 días", value: 7 };
    await Filters.value.$refs.filterButton.$el.click();
  }
  await setDataInSumarioList(effectiveSearch);
  processLocations();
}

async function getByRange(search) {
  Filters.value.filters = Filters.value.filters.filter((filter) => filter.name !== "Actuaciones");
  if (search === undefined) getByPredeterminedDays();
  await setDataInSumarioList(search);
  processLocations();
}

async function getByActuationType(searchValue) {
  const getOptions = {
    url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/actuacion?getTotal=false&search=${searchValue}`,
  };
  const resp = await rest.get(getOptions);
  map.value.markers = formatLocations(resp.data.data);
  actuationsList.value = formatLocations(resp.data.data, "List");
  map.value.$refs.LControlRef.$el.remove();
  loadingQuery.value = false;
}

async function getData() {
  loadingQuery.value = true;
  const searchForDetermineFilterType = await Filters.value.getFiltersV2()?.search;
  const searchForQuery = await normalizeSearch(await Filters.value.getFiltersV2());
  map.value.$refs.LControlRef.$el.remove();
  if (searchForDetermineFilterType?.includes("exp_pre_TipoActuacion.TipoActuacionID")) {
    getByActuationType(searchForQuery);
    return;
  }
  if (searchForDetermineFilterType?.includes("createdAt") && Filters.value.value === null) {
    getByRange(searchForQuery);
  } else {
    await getByPredeterminedDays(searchForQuery);
  }
  await determineUseFromCloseFilterButton();
}

const getClass = (state) => {
  switch (state) {
    case "En Curso":
      return "bg-green-200 flex justify-content-center white-space-nowrap p-1 font-medium text-green-800 border-round";
    case "Vencida":
      return "bg-yellow-200 flex justify-content-center white-space-nowrap p-1 font-medium text-yellow-800 border-round";
    case "Finalizada":
      return "custom-bg-red-200 flex justify-content-center white-space-nowrap p-1 font-medium custom-text-red-800 border-round";
  }
};

const highlightActuationOnMap = () => {
  if (!Number.isInteger(actuationSelected.value)) {
    centerTheMapToDefaultPosition();
    map.value.selectedItem = null;
    map.value.mapKey = map.value.mapKey + 1;
    setTimeout(() => {
      map.value.$refs.LControlRef.$el.remove();
    }, 100);
    return;
  }
  map.value.selectedItem = actuationSelected.value;
  map.value.center = map.value.markers[actuationSelected.value].position;
  map.value.zoom = 15;
  map.value.mapKey = map.value.mapKey + 1;
  setTimeout(() => {
    map.value.$refs.LControlRef.$el.remove();
  }, 100);
};

const showExportSuccessToast = () => {
  toast.open({
    message: "Exportación exitosa. El archivo ha sido descargado correctamente.",
    position: "top-right",
    type: "success",
    duration: 3000,
  });
};

const showExportFailToast = () => {
  toast.open({
    message: "Error en la exportación. No se pudo generar el archivo.",
    position: "top-right",
    type: "error",
    duration: 3000,
  });
};

const processJPG = async () => {
  const mapElement = map.value.$el;
  try {
    const canvas = await html2canvas(mapElement, { useCORS: true });
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/jpeg");
    link.download = "map.jpg";
    link.click();
    showExportSuccessToast();
  } catch (error) {
    showExportFailToast();
    console.error("Error al exportar el mapa:", error);
  }
};

const processPDF = async () => {
  const mapElement = map.value.$el;
  try {
    const scale = 2;
    const canvas = await html2canvas(mapElement, {
      useCORS: true,
      backgroundColor: "#ffffff",
      scale,
    });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("landscape", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const pxToMm = (px) => (px * 25.4) / 96;
    const imgWidthMm = pxToMm(canvas.width);
    const imgHeightMm = pxToMm(canvas.height);
    const ratio = Math.min(pageWidth / imgWidthMm, pageHeight / imgHeightMm);
    const displayWidth = imgWidthMm * ratio;
    const displayHeight = imgHeightMm * ratio;
    const x = (pageWidth - displayWidth) / 2;
    const y = (pageHeight - displayHeight) / 2;
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pageWidth, pageHeight, "F");
    pdf.addImage(imgData, "PNG", x, y, displayWidth, displayHeight);
    pdf.save("map.pdf");
    showExportSuccessToast();
  } catch (error) {
    console.error("Error al exportar el mapa a PDF:", error);
    showExportFailToast();
  }
};

const exportToCSV = async () => {
  try {
    const csvContent =
      `data:text/csv;charset=utf-8,` +
      `Nombre, Nro de actuacion, Caratula/Causa, Latitud,Longitud\n${actuationsList.value
        .map(
          (marker) =>
            `${marker.name}, ${marker.number}, ${marker.caseTitle}, ${marker.position.lat},${marker.position.lng}`
        )
        .join("\n")}`;
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", "map.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showExportSuccessToast();
  } catch (error) {
    console.error("Error al exportar el mapa a PDF:", error);
    showExportFailToast();
  }
};

const captureFormatList = [
  { name: "JPG", action: () => processJPG() },
  { name: "PDF", action: () => processPDF() },
  { name: "CSV", action: () => exportToCSV() },
];

const handleSelection = () => {
  captureFormat.value();
  captureFormat.value = null;
};

onBeforeRouteLeave((to, from, next) => {
  console.log("beforeRouteLeave", to.path);
  if (to.path === "/actuaciones/list") {
    window.location.href = "/actuaciones/list";
  } else {
    next();
  }
});

onMounted(() => {
  centerTheMapToDefaultPosition();
  setTimeout(() => {
    Filters.value.value = { name: "7 días", value: 7 };
    Filters.value.$refs.filterButton.$el.click();
  }, 100);
  dropdown.value["exp_pre_TipoActuacion.TipoActuacionID"].options = filterDropdownOptions.value;
});
</script>

<style scoped>
.map {
  display: block;
  height: 70vh;
  width: 100%;
}
.dialog-over-map {
  background-color: #e0dfdf;
  padding: 2px 10px;
  color: #34495e;
}
.dialog-empty-actuations {
  background-color: #d32f2f;
  padding: 5px 10px;
  color: #f8e0e0;
  border: 1px solid #c0392b;
}
.dialog {
  border-radius: 2px;
  display: flex;
  align-items: center;
  font-family: Arial, sans-serif;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.header-actuaciones-list {
  background-color: rgb(248 249 250);
  border: 1px solid #ddd;
  padding: 10px;
  border-radius: 4px;
  height: 45px;
}
.content-actuaciones-list {
  border: 1px solid #ddd;
  padding: 0;
  border-radius: 4px;
  height: 100%;
}
.custom-bg-red-200 {
  background-color: #ffada7;
}
.custom-text-red-800 {
  color: #8c231c;
}
.block-container {
  position: relative;
  padding: 1em;
}
.custom-spinner {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 4rem;
  color: #3b4754;
  z-index: 1100;
}
.container-items {
  padding-right: 1em;
}

>>> .capture-format-dropdown {
  background-color: #2196f3 !important;
  border-color: #1976d2 !important;
  color: white !important;
}

>>> .capture-format-dropdown .p-dropdown-label {
  color: white !important;
}

>>> .capture-format-dropdown.p-focus {
  background-color: #1976d2 !important;
  border-color: #1565c0 !important;
}

>>> .capture-format-dropdown .p-dropdown-panel {
  background-color: white !important;
  border-color: #2196f3 !important;
}

>>> .capture-format-dropdown .p-dropdown-item {
  color: #7d858c !important;
}

>>> .capture-format-dropdown .p-dropdown-item:hover {
  background-color: #2196f3 !important;
  color: white !important;
}

>>> .capture-format-dropdown .p-dropdown-trigger {
  color: white !important;
}

>>> .capture-format-dropdown .p-dropdown-trigger svg {
  fill: white !important;
}

>>> .p-card .p-card-body {
  padding: 0;
}

>>> .p-card .p-card-content {
  padding: 0;
  height: 70vh;
  display: flex;
  flex-direction: column;
}
>>> .leaflet-pane,
>>> .leaflet-map-pane,
>>> .leaflet-tile-pane,
>>> .leaflet-overlay-pane,
>>> .leaflet-shadow-pane,
>>> .leaflet-marker-pane,
>>> .leaflet-tooltip-pane,
>>> .leaflet-popup-pane {
  z-index: 0 !important;
}
</style>
